import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import axios from 'axios';
import confetti from 'canvas-confetti';
import {
  selectedClientSelector,
  setSelectedClient,
  updateSelectedClientName
} from '../../store/slices/selectedClient';
import {
  getClientById,
  normalizeClientResponse
} from '../../store/slices/clients';
import {
  getGoogleOAuthRedirectUri,
  initiateGoogleOAuth
} from '../../services/googleOAuth';
import { getGoogleProfileInfo } from '../../components/GoogleBusinessProfile/services';
import {
  API_CLIENTS,
  API_KEY,
  API_NEWCLIENT,
  API_ZOHO_WEBSITE_NOTE,
  AWS_BUCKET_CLIENT_LOGOS,
  BUCKET_CS_IMAGES_UPLOAD,
  S3_GET_PRESIGNED_URLS,
  UPDATE_STOCK_CLIENT_UPLOAD_IMAGES
} from '../../utils/constants';
import { v4 as uuid } from 'uuid';
import { convertHeicToJpeg } from '../Images/convertHeicToJpeg';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import CreatableSelect from 'react-select/creatable';
import { Checkbox } from '../../components/ui/checkbox';
import { Progress } from '../../components/ui/progress';
import { RadioGroup, RadioGroupItem } from '../../components/ui/radio-group';
import GbpOffersPicker, {
  createGbpOffersValue
} from '../../components/GbpOffersPicker';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '../../components/ui/select';
import {
  ArrowLeft,
  ArrowRight,
  Check,
  ChevronDown,
  ChevronUp,
  ClipboardList,
  ExternalLink,
  Pencil,
  Upload,
  X,
  LogIn,
  Info,
  Loader2,
  AlertCircle
} from 'lucide-react';
import { initiateFacebookOAuth } from '../../services/facebookOAuth';
import {
  BusinessHoursSelect,
  DAYS as WEEK_DAYS,
  defaultWeekHours,
  cloneWeekHours
} from '../../components/BusinessHoursSelect/index.tsx';
import {
  canContinueAfterGoogleConnection,
  hasGoogleBusinessConnection,
  mapGoogleLocationToAnswers
} from './googleLocation';
import {
  applyClientFlagChanges,
  buildOnboardingStepUpdate
} from './clientPayload';
import {
  mapClientToOnboardingAnswers,
  mergeGoogleAnswersIntoEmptyFields
} from './clientAnswers';
import {
  HOME_SERVICES_TYPE,
  getBusinessCategoryOptions,
  OTHER_CATEGORY,
  TYPE_OPTIONS
} from './businessCategories';
import {
  buildZohoOnboardingRequest,
  submitWebsitePreferences
} from '../../services/submitWebsitePreferences';
import logo from '../../assets/logo.png';
import layoutHvac from '../../assets/layout-hvac.jpg';
import layoutPlumber from '../../assets/layout-plumber.jpg';
import layoutElectrician from '../../assets/layout-electrician.jpg';

const S_DETAILS = 'Business Details';
const S_HOURS = 'Hours & Services';
const S_POSTS = 'Your Social Media Posts';
const S_TEAM = 'Your Team';
const S_ACCESS = 'Account Access';
const S_GOALS = 'Goals and Expectations';
const ONBOARDING_OAUTH_RESULT_KEY = 'google_onboarding_oauth_result';

const getTagValues = (value) =>
  String(value ?? '')
    .split(/\r?\n/)
    .map((item) => item.trim())
    .filter(Boolean)
    .map((label) => ({ label, value: label }));

const onboardingTagStyles = {
  control: (provided, state) => ({
    ...provided,
    minHeight: 42,
    borderRadius: '6px',
    borderColor: state.isFocused ? '#3B5998' : '#e2e8f0',
    boxShadow: 'none',
    '&:hover': { borderColor: '#3B5998' }
  }),
  valueContainer: (provided) => ({
    ...provided,
    padding: '4px 8px',
    gap: '4px'
  }),
  input: (provided) => ({
    ...provided,
    margin: '2px',
    padding: '2px'
  }),
  placeholder: (provided) => ({
    ...provided,
    color: '#737373'
  }),
  indicatorSeparator: () => ({ display: 'none' }),
  dropdownIndicator: () => ({ display: 'none' }),
  multiValue: (provided) => ({
    ...provided,
    backgroundColor: '#f4f4f4',
    borderRadius: '9999px',
    margin: '2px 4px',
    padding: '0 4px'
  }),
  multiValueLabel: (provided) => ({
    ...provided,
    color: '#0f172a',
    fontSize: '0.875rem'
  }),
  multiValueRemove: (provided) => ({
    ...provided,
    borderRadius: '9999px',
    '&:hover': {
      backgroundColor: '#e2e8f0',
      color: '#0f172a'
    }
  })
};

const TIMEZONES = [
  'Eastern',
  'Central',
  'Mountain',
  'Mountain – Arizona (no daylight saving)',
  'Pacific',
  'Alaska',
  'Hawaii'
];

const US_HOLIDAYS = [
  "New Year's Day",
  'Martin Luther King Jr. Day',
  "Presidents' Day",
  'Memorial Day',
  'Juneteenth',
  'Independence Day',
  'Labor Day',
  'Columbus Day',
  'Veterans Day',
  'Thanksgiving Day',
  'Christmas Day'
];
const CA_HOLIDAYS = [
  'Victoria Day',
  'Canada Day',
  'Canadian Thanksgiving',
  'Remembrance Day'
];
const ALL_HOLIDAYS = [...US_HOLIDAYS, ...CA_HOLIDAYS];

const GOALS = [
  'Get more phone calls from potential customers',
  'Generate more leads and service requests',
  'Book more jobs and appointments',
  'Rank higher on Google Search and Maps',
  'Get found and recommended in AI searches',
  'Get more customer reviews',
  'Build a stronger online reputation',
  'Increase brand awareness in my local area',
  'Expand into new service areas',
  'Grow my business and increase revenue'
];

const noWebsite = (v) => !!v.website?.none;

const QUESTIONS = [
  {
    id: 'name',
    section: S_DETAILS,
    type: 'text',
    title:
      "What's your business name? Enter it exactly as you want it to appear on Google.",
    description:
      "Use your real business name only. Don't add keywords or city names — Google can suspend listings that do.",
    placeholder: 'e.g. Smith Family Plumbing'
  },
  {
    id: 'logo',
    section: S_DETAILS,
    type: 'file',
    title: 'Upload your business logo.',
    description: 'No logo? Skip this — we can help.'
  },
  {
    id: 'phone',
    section: S_DETAILS,
    type: 'phone',
    title: "What's your main business phone number?",
    description: 'This is the number customers will call.',
    placeholder: '(555) 123-4567'
  },
  {
    id: 'phone2',
    section: S_DETAILS,
    type: 'phone',
    title: 'Second phone number (optional)',
    description: 'For example, a cell or after-hours line.',
    placeholder: '(555) 987-6543'
  },
  {
    id: 'phoneShown',
    section: S_DETAILS,
    type: 'radio',
    title: 'Which number should we show on Google and your other listings?',
    options: ['Main number', 'Second number'],
    show: (v) => !!v.phone2?.trim()
  },
  {
    id: 'businessType',
    section: S_DETAILS,
    type: 'businessType',
    title: 'Which category best describes your business?'
  },
  {
    id: 'address',
    section: S_DETAILS,
    type: 'address',
    title: "What's your business address?",
    description:
      'Use the address on your Google listing. Work from home? Tell us and we can hide your address on Google.'
  },
  {
    id: 'timezone',
    section: S_DETAILS,
    type: 'radio',
    title: 'What time zone is your business in?',
    options: TIMEZONES
  },
  {
    id: 'areas',
    section: S_DETAILS,
    type: 'tags',
    title: 'Which cities or towns do you serve?',
    description:
      'Type each city or town and press Enter. Most important first — Google shows up to 20.',
    placeholder: 'Type a city and press Enter...'
  },
  {
    id: 'website',
    section: S_DETAILS,
    type: 'website',
    title: "What's your website address?",
    placeholder: 'www.yourbusiness.com'
  },
  {
    id: 'email',
    section: S_DETAILS,
    type: 'email',
    title: 'What email address should customers use to contact you?',
    placeholder: 'you@yourbusiness.com'
  },
  {
    id: 'special',
    section: S_DETAILS,
    type: 'textarea',
    title: 'What makes your business stand out? Tell us in a few sentences.',
    description:
      'For example: years in business, family-owned, licensed and insured, warranties, same-day service, awards.'
  },
  {
    id: 'licensed',
    section: S_DETAILS,
    type: 'radio',
    title: 'Are you licensed and insured?',
    options: [
      'Licensed and insured',
      'Licensed only',
      'Insured only',
      'Neither'
    ],
    followUp: {
      when: (o) => o.toLowerCase().includes('licensed'),
      label: "What's your license number?"
    }
  },
  {
    id: 'yearStarted',
    section: S_DETAILS,
    type: 'text',
    title: 'What year did you start your business?',
    placeholder: 'e.g. 2012'
  },

  {
    id: 'hours',
    section: S_HOURS,
    type: 'hours',
    title: 'What are your business hours?',
    description: "Turn off any day you're not open, or check Open 24 hours."
  },
  {
    id: 'services',
    section: S_HOURS,
    type: 'tags',
    title: 'List every service you offer.',
    description:
      'Type each service and press Enter. Be specific — "water heater repair" and "water heater installation" are separate services. A complete list helps you show up for more searches.',
    placeholder: 'Type a service and press Enter...'
  },
  {
    id: 'priorityServices',
    section: S_HOURS,
    type: 'tags',
    title: 'Which 3 services do you want more calls for?',
    description:
      "Type each service and press Enter. Usually your most profitable jobs. We'll focus on these first.",
    placeholder: 'Type a service and press Enter...'
  },

  {
    id: 'specials',
    section: S_POSTS,
    type: 'specialOffers',
    title: 'Which special offers would you like us to promote?',
    description:
      "We've suggested five offers proven to generate high conversions for businesses in your industry. Select the ones you'd like us to promote, customize the discounts or percentages, or add your own offers."
  },
  {
    id: 'estimates',
    section: S_POSTS,
    type: 'checkboxes',
    title: 'Do you offer free estimates or financing?',
    description: 'Check all that apply.',
    options: ['Free estimates', 'Financing', 'Neither'],
    exclusive: 'Neither'
  },
  {
    id: 'avoid',
    section: S_POSTS,
    type: 'textarea',
    title: 'Is there anything we should never mention in your posts?',
    description:
      'For example: services you no longer offer, prices, competitor names. Leave blank if none.'
  },
  {
    id: 'holidays',
    section: S_POSTS,
    type: 'holidays',
    title: 'Which holidays would you like us to post about?',
    description: 'Check all that apply.'
  },
  {
    id: 'platforms',
    section: S_POSTS,
    type: 'checkboxes',
    title: 'Where should we post for you?',
    description: 'Check all that apply.',
    options: ['Google', 'Facebook', 'Instagram', 'TikTok']
  },
  {
    id: 'images',
    section: S_POSTS,
    type: 'radio',
    title: 'What kind of images should we use in your posts?',
    options: [
      'Real photos when I have them, AI images to fill the gaps',
      'Real photos only'
    ],
    note: {
      when: (o) => o === 'Real photos only',
      text: 'Please upload photos in the next steps so we have enough to post.'
    }
  },
  {
    id: 'photos',
    section: S_POSTS,
    type: 'files',
    title: 'Upload photos of your business.',
    description:
      'Please upload as many high-quality photos as possible to help us showcase your business across Google, social media, and your website (if applicable).\n\nInclude photos of your team, employees at work, company vehicles, equipment, business location, completed projects, before-and-after transformations, customer interactions, awards, and community involvement.\n\nThe more photos you provide, the better we can showcase your business!'
  },
  {
    id: 'hashtags',
    section: S_POSTS,
    type: 'text',
    title: 'Any hashtags you want us to use?',
    description:
      "For example: #SmithPlumbing. Leave blank and we'll pick them for you.",
    placeholder: '#SmithPlumbing #MiamiPlumber'
  },

  {
    id: 'teamSize',
    section: S_TEAM,
    type: 'radio',
    title: 'How many people work in your business, including you?',
    options: ['Just me', '2–9', '10–19', 'More than 20']
  },

  {
    id: 'gbpManager',
    section: S_ACCESS,
    type: 'fields',
    title: 'Who manages your Google Business Profile right now?',
    fields: [
      { label: 'Their name' },
      { label: 'Email they use to log in to Google' }
    ],
    unsureLabel: "I'm not sure"
  },
  {
    id: 'domain',
    section: S_ACCESS,
    type: 'fields',
    title: 'Who is your domain registered with?',
    fields: [
      {
        label: 'Domain company',
        placeholder: 'e.g. GoDaddy, Namecheap, Google Domains'
      },
      { label: 'Domain account username' },
      { label: 'Domain account password', password: true }
    ],
    show: (v) => !noWebsite(v)
  },
  {
    id: 'socialLinks',
    section: S_ACCESS,
    type: 'fields',
    title: 'Links to your Facebook, Instagram, and TikTok pages',
    fields: [
      { label: 'Facebook page link', noneLabel: "I don't have one" },
      { label: 'Instagram page link', noneLabel: "I don't have one" },
      { label: 'TikTok page link', noneLabel: "I don't have one" }
    ]
  },

  {
    id: 'goals',
    section: S_GOALS,
    type: 'checkboxes',
    title: 'What are your main goals with Adler Local?',
    description: 'Select up to 2 options.',
    options: GOALS,
    max: 2
  },
  {
    id: 'competitors',
    section: S_GOALS,
    type: 'fields',
    title: 'Who are your 2–3 biggest local competitors?',
    description:
      'Business names are fine. Add their websites if you know them.',
    fields: [
      { label: 'Competitor 1' },
      { label: 'Competitor 2' },
      { label: 'Competitor 3' }
    ]
  }
];

const LAYOUTS = [
  {
    name: 'Layout 1',
    url: 'https://hvac.adlerlaunchpad.com/',
    img: layoutHvac
  },
  {
    name: 'Layout 2',
    url: 'https://plumber.adlerlaunchpad.com/',
    img: layoutPlumber
  },
  {
    name: 'Layout 3',
    url: 'https://electrician.adlerlaunchpad.com/',
    img: layoutElectrician
  }
];

const WEBSITE_CONTENT_OPTIONS = [
  'Keep my wording and improve it for Google',
  'Write all-new content for me'
];

const getGoogleLocationKey = (location, index) =>
  String(location.name || location.id || location.locationId || index);

const getGoogleLocationLabel = (location) =>
  location.title ||
  location.locationName ||
  location.name ||
  location.address ||
  `Business location`;

const defaultHours = () => ({
  days: cloneWeekHours(defaultWeekHours())
});

/** Turns a stored answer into readable text for the review screen. */
const formatAnswer = (question, val, allAnswers = {}) => {
  if ((val == null || val === '') && question.type !== 'specialOffers') {
    return null;
  }
  switch (question.type) {
    case 'text':
    case 'phone':
    case 'email':
    case 'textarea':
      return String(val).trim() || null;
    case 'website':
      return val.none ? "I don't have a website" : val.url?.trim() || null;
    case 'address': {
      const parts = [val.street, val.suite].filter(Boolean);
      return parts.length ? parts.join(', ') : null;
    }
    case 'file':
    case 'files': {
      const files = val;
      return files.length ? files.map((f) => f.name).join(', ') : null;
    }
    case 'radio': {
      if (typeof val === 'object') {
        const choice = val.choice ?? '';
        if (!choice) return null;
        return val.extra?.trim() ? `${choice} — ${val.extra}` : choice;
      }
      return String(val).trim() || null;
    }
    case 'businessType': {
      const choice = val.choice || '';
      if (!choice) return null;
      return choice === OTHER_CATEGORY && val.other?.trim()
        ? `${choice} — ${val.other.trim()}`
        : choice;
    }
    case 'gbpOffers': {
      return formatSelectedGbpOffers(val);
    }
    case 'specialOffers': {
      const specials = String(val ?? '').trim();
      const gbpOffers = formatSelectedGbpOffers(allAnswers.gbpOffers);
      return [specials, gbpOffers].filter(Boolean).join('\n') || null;
    }
    case 'checkboxes':
    case 'holidays': {
      const list = val;
      return list.length ? list.join(', ') : null;
    }
    case 'hours': {
      const h = val;
      const lines = WEEK_DAYS.map((d) => {
        const day = h.days?.[d.key];
        if (!day) return null;
        if (!day.isOpen) return `${d.label}: Closed`;
        if (day.open24) return `${d.label}: Open 24 hours`;
        if (day.openTime && day.closeTime)
          return `${d.label}: ${day.openTime} – ${day.closeTime}`;
        return null;
      }).filter(Boolean);
      if (h.emergency) lines.push(`Emergency service: ${h.emergency}`);
      return lines.length ? lines.join('\n') : null;
    }
    case 'fields': {
      const data = val;
      if (data.unsure) return "I'm not sure";
      const lines = question.fields
        .map((f, i) => {
          if (data.none?.[i]) return `${f.label}: ${f.noneLabel ?? 'None'}`;
          const value = f.password
            ? data.vals?.[i]
              ? '••••••••'
              : ''
            : data.vals?.[i]?.trim();
          return value ? `${f.label}: ${value}` : null;
        })
        .filter(Boolean);
      return lines.length ? lines.join('\n') : null;
    }
    default:
      return null;
  }
};

const formatSelectedGbpOffers = (value) => {
  const selectedOffers = value?.selectedOffers ?? [];
  return (
    selectedOffers
      .map((index) => value.offers?.[index])
      .filter(Boolean)
      .join('\n') || null
  );
};

const choiceCls =
  'flex items-center gap-3 rounded-xl border p-4 cursor-pointer hover:bg-muted/50 transition-colors';

const getGoogleLogoSource = (location) =>
  location?.images_info?.logoDataUrl ||
  location?.images_info?.logoUrl ||
  location?.logoUrl;

const downloadGoogleLogo = async (url) => {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(
      `Google logo download failed with status ${response.status}`
    );
  }

  const blob = await response.blob();
  const type = blob.type || 'image/jpeg';
  if (!type.startsWith('image/')) {
    throw new Error('Google profile photo is not an image.');
  }

  const extension =
    {
      'image/jpeg': 'jpg',
      'image/png': 'png',
      'image/webp': 'webp',
      'image/gif': 'gif',
      'image/svg+xml': 'svg'
    }[type] || 'jpg';

  return new File([blob], `google-business-logo.${extension}`, { type });
};

const uploadLogoToS3WithClientId = async (file, clientId) => {
  const fileType = file.type;
  const fileExtension = file.name.split('.').pop();
  const fileName = `${clientId}/logo.${fileExtension}`;
  const presignedResponse = await fetch(S3_GET_PRESIGNED_URLS, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      bucketName: AWS_BUCKET_CLIENT_LOGOS,
      files: [{ fileName, fileType }]
    })
  });

  if (!presignedResponse.ok) {
    throw new Error('Failed to get a presigned URL for the logo.');
  }

  const { urls } = await presignedResponse.json();
  const presignedUrl = urls?.[0]?.url;
  if (!presignedUrl) {
    throw new Error('No presigned URL was returned for the logo.');
  }

  const uploadResponse = await fetch(presignedUrl, {
    method: 'PUT',
    headers: { 'Content-Type': fileType },
    body: file
  });
  if (!uploadResponse.ok) {
    throw new Error('The logo could not be uploaded to storage.');
  }

  return `https://${AWS_BUCKET_CLIENT_LOGOS}.s3.amazonaws.com/${fileName}?v=${Date.now()}`;
};

const FileThumb = ({ file, onRemove }) => {
  const [previewUrl, setPreviewUrl] = useState('');

  useEffect(() => {
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  return (
    <div className="relative aspect-square overflow-hidden rounded-xl border bg-muted">
      {previewUrl && (
        <img
          src={previewUrl}
          alt={file.name}
          className="h-full w-full object-cover"
        />
      )}
      <button
        type="button"
        onClick={onRemove}
        aria-label={`Remove ${file.name}`}
        className="absolute right-1 top-1 flex h-6 w-6 items-center justify-center rounded-full bg-background/90 shadow hover:bg-background">
        <X className="h-4 w-4" />
      </button>
    </div>
  );
};

// Same flow as the Images page: presigned S3 upload + register as manual_upload.
const uploadPhotoToGallery = async (originalFile, clientId) => {
  const file = await convertHeicToJpeg(originalFile);
  const fileType = file.type;
  const sanitizedFileName = file.name
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-zA-Z0-9.-]/g, '_')
    .replace(/\s+/g, '_');
  const fileName = `${uuid()}-${sanitizedFileName}`;

  const presignedResponse = await fetch(S3_GET_PRESIGNED_URLS, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      bucketName: BUCKET_CS_IMAGES_UPLOAD,
      files: [{ fileName, fileType }]
    })
  });
  if (!presignedResponse.ok) throw new Error('Failed to get presigned URL');

  const { urls } = await presignedResponse.json();
  const presignedUrl = urls?.[0]?.url;
  if (!presignedUrl) throw new Error('No presigned URL returned');

  const uploadResult = await fetch(presignedUrl, {
    method: 'PUT',
    headers: { 'Content-Type': fileType },
    body: file
  });
  if (!uploadResult.ok) throw new Error('File upload to S3 failed');

  const uploadedFileUrl = `https://${BUCKET_CS_IMAGES_UPLOAD}.s3.amazonaws.com/${fileName}`;

  await axios.post(
    UPDATE_STOCK_CLIENT_UPLOAD_IMAGES,
    {
      clientId,
      sourceUrl: uploadedFileUrl,
      source: 'manual_upload',
      keywords: '',
      lastUse: Math.floor(Date.now() / 1000)
    },
    {
      headers: {
        'X-API-KEY': API_KEY,
        'Content-Type': 'application/json'
      }
    }
  );
};

const Onboarding = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const { selectedClient } = useSelector(selectedClientSelector);
  const shouldVerifyGoogleConnection =
    history.location.state?.verifyGoogleConnection === true;
  const pendingGoogleLocation = history.location.state?.pendingGoogleLocation;
  const onboardingClientRef = useRef(selectedClient || {});
  // The form always starts empty; Adler Suite data is never used to prefill it.
  const initialClientData = mapClientToOnboardingAnswers({});
  const [step, setStep] = useState(0);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [finished, setFinished] = useState(false);
  const [clientRefreshLoading, setClientRefreshLoading] = useState(true);
  const [clientRefreshError, setClientRefreshError] = useState('');
  const [clientRefreshAttempt, setClientRefreshAttempt] = useState(0);
  const [showReview, setShowReview] = useState(false);
  const [editingFromReview, setEditingFromReview] = useState(false);
  const celebratedRef = useRef(false);
  const [values, setValues] = useState(() => initialClientData.answers);
  const logoFile = values.logo?.[0];
  const [logoPreviewUrl, setLogoPreviewUrl] = useState('');
  const [googleConnecting, setGoogleConnecting] = useState(false);
  // Google connection state comes only from the backend, never from Redux.
  const [googleConnected, setGoogleConnected] = useState(false);
  const [serverGoogleClient, setServerGoogleClient] = useState(null);
  const [googleProfileLoading, setGoogleProfileLoading] = useState(false);
  const [googleLocations, setGoogleLocations] = useState([]);
  const [selectedGoogleLocationKey, setSelectedGoogleLocationKey] =
    useState('');
  const [googleError, setGoogleError] = useState('');
  const [googleLogoError, setGoogleLogoError] = useState('');
  const [googleLogoLoading, setGoogleLogoLoading] = useState(false);
  const [googleConnectionCheckStatus, setGoogleConnectionCheckStatus] =
    useState('idle');
  const [googleConnectionCheckRetry, setGoogleConnectionCheckRetry] =
    useState(0);
  const googlePopupRef = useRef(null);
  const facebookPopupWatcherRef = useRef(null);
  const googlePopupCloseTimeoutRef = useRef(null);
  const googlePopupOriginRef = useRef(null);
  const googleOAuthResultHandledRef = useRef(false);
  const logoDownloadPromisesRef = useRef(new Map());
  const logoRequestRef = useRef(0);
  const uploadedPhotosRef = useRef(new WeakSet());
  const [connected, setConnected] = useState({
    google: false,
    facebook: false
  });
  const [web, setWeb] = useState(() => initialClientData.websiteAnswers);
  const websiteNoteInFlightRef = useRef(false);
  const websiteNoteOutcomeRef = useRef('idle');
  const websiteNoteIdRef = useRef(null);
  const websiteCaseIdRef = useRef(null);

  useEffect(() => {
    if (!selectedClient) return;

    onboardingClientRef.current = {
      ...onboardingClientRef.current,
      ...selectedClient
    };
  }, [selectedClient]);

  const selectedClientId = selectedClient?.id;

  useEffect(() => {
    let active = true;
    if (!selectedClientId) {
      setClientRefreshLoading(false);
      setClientRefreshError('Select a client before starting onboarding.');
      return () => {
        active = false;
      };
    }

    setClientRefreshLoading(true);
    setClientRefreshError('');
    dispatch(getClientById({ clientId: selectedClientId }))
      .unwrap()
      .then((freshClient) => {
        if (!active) return;
        if (freshClient?.id !== selectedClientId) {
          throw new Error(
            'Client detail response does not match the selected client.'
          );
        }
        if (
          !Object.prototype.hasOwnProperty.call(
            freshClient,
            'websiteOnboardingRequired'
          )
        ) {
          throw new Error(
            'Client detail response is missing website onboarding eligibility.'
          );
        }
        dispatch(setSelectedClient(freshClient));
        setClientRefreshLoading(false);
      })
      .catch((error) => {
        if (!active) return;
        setClientRefreshLoading(false);
        setClientRefreshError(
          error?.message ||
            'Unable to refresh this client. Retry before starting onboarding.'
        );
      });

    return () => {
      active = false;
    };
  }, [dispatch, selectedClientId, clientRefreshAttempt]);

  const applyGoogleLocation = useCallback((location) => {
    setValues((currentValues) =>
      mergeGoogleAnswersIntoEmptyFields(
        currentValues,
        mapGoogleLocationToAnswers(location)
      )
    );

    const requestId = ++logoRequestRef.current;
    setGoogleLogoError('');
    const logoSource = getGoogleLogoSource(location);
    if (!logoSource) {
      setGoogleLogoLoading(false);
      return;
    }

    setGoogleLogoLoading(true);
    let downloadPromise = logoDownloadPromisesRef.current.get(logoSource);
    if (!downloadPromise) {
      downloadPromise = downloadGoogleLogo(logoSource);
      logoDownloadPromisesRef.current.set(logoSource, downloadPromise);
      downloadPromise.then(
        () => logoDownloadPromisesRef.current.delete(logoSource),
        () => logoDownloadPromisesRef.current.delete(logoSource)
      );
    }

    downloadPromise
      .then((file) => {
        if (logoRequestRef.current !== requestId) return;
        setGoogleLogoLoading(false);
        setValues((currentValues) =>
          currentValues.logo?.length || currentValues.logoUrl
            ? currentValues
            : { ...currentValues, logo: [file] }
        );
      })
      .catch((error) => {
        if (logoRequestRef.current !== requestId) return;
        setGoogleLogoLoading(false);
        console.error('Error downloading Google Business Profile logo:', error);
        setGoogleLogoError(
          'Google connected, but its profile logo could not be downloaded. You can upload it manually.'
        );
      });
  }, []);

  // Switching clients must never inherit the previous client's Google state.
  useEffect(() => {
    logoRequestRef.current += 1;
    setValues(mapClientToOnboardingAnswers({}).answers);
    setWeb(mapClientToOnboardingAnswers({}).websiteAnswers);
    setStep(0);
    setFinished(false);
    setShowReview(false);
    setEditingFromReview(false);
    websiteNoteInFlightRef.current = false;
    websiteNoteOutcomeRef.current = 'idle';
    websiteNoteIdRef.current = null;
    websiteCaseIdRef.current = null;
    setGoogleLogoError('');
    setGoogleLogoLoading(false);
    setServerGoogleClient(null);
    setGoogleConnected(false);
    setConnected((current) => ({ ...current, google: false }));
    setGoogleLocations([]);
    setSelectedGoogleLocationKey('');
    setGoogleError('');
    setGoogleConnectionCheckStatus('idle');
    return () => {
      logoRequestRef.current += 1;
    };
  }, [selectedClientId]);

  useEffect(() => {
    if (!serverGoogleClient || !hasGoogleBusinessConnection(serverGoogleClient))
      return undefined;

    let active = true;
    const clientId = serverGoogleClient.id;
    const savedLocationName =
      serverGoogleClient.socialnetworks?.googleMyBusinessLocationName ||
      pendingGoogleLocation?.locationName ||
      'Connected Google Business Profile';
    const savedLocationId =
      serverGoogleClient.socialnetworks?.googleMyBusinessLocationId ||
      serverGoogleClient.socialnetworks?.googleLocationId ||
      pendingGoogleLocation?.id;
    const savedLocation = {
      id: savedLocationId || clientId,
      name: savedLocationId || clientId,
      title: savedLocationName,
      locationName: savedLocationName
    };

    setGoogleConnected(true);
    setConnected((current) => ({ ...current, google: true }));
    setGoogleLocations([savedLocation]);
    setSelectedGoogleLocationKey(getGoogleLocationKey(savedLocation, 0));
    setGoogleError('');
    setGoogleProfileLoading(true);

    const loadAssignedGoogleProfile = async () => {
      try {
        const profileInfo = await getGoogleProfileInfo(clientId);
        const profile = profileInfo.data?.data || profileInfo.data;
        const profileAnswers = mapGoogleLocationToAnswers(profile);
        if (!active) return;

        if (
          Object.keys(profileAnswers).length > 0 ||
          getGoogleLogoSource(profile)
        ) {
          const profileLocation = {
            ...profile,
            ...savedLocation
          };
          setGoogleLocations([profileLocation]);
          setSelectedGoogleLocationKey(
            getGoogleLocationKey(profileLocation, 0)
          );
          applyGoogleLocation(profileLocation);
        } else {
          setGoogleError(
            'Google Business is already connected. Profile details could not be loaded, so you can fill them in manually.'
          );
        }
      } catch (error) {
        console.error('Error loading assigned Google profile:', error);
        if (active) {
          setGoogleError(
            'Google Business is already connected. Profile details could not be loaded, so you can fill them in manually.'
          );
        }
      } finally {
        if (active) setGoogleProfileLoading(false);
      }
    };

    loadAssignedGoogleProfile();
    return () => {
      active = false;
    };
  }, [applyGoogleLocation, pendingGoogleLocation, serverGoogleClient]);

  useEffect(() => {
    if (!selectedClientId) return undefined;

    const clientId = selectedClientId;
    let active = true;
    let timeoutId;
    let attempts = 0;
    // Retry only while waiting for a connection that was just made.
    const maxAttempts = shouldVerifyGoogleConnection ? 8 : 1;
    const retryDelay = 3000;

    const checkConnection = async () => {
      if (!active) return;
      setGoogleConnectionCheckStatus('checking');

      try {
        const response = await axios.get(`${API_CLIENTS}/${clientId}`, {
          headers: { 'X-API-KEY': API_KEY },
          timeout: 10000
        });
        const clientData = normalizeClientResponse(response.data);
        if (clientData.id !== clientId) {
          throw new Error('Google detail response does not match the client.');
        }

        if (hasGoogleBusinessConnection(clientData)) {
          const serverSocialNetworks = clientData.socialnetworks || {};
          const googleNetworks = {
            googleMyBusiness: true,
            googleMyBusinessLocationId:
              serverSocialNetworks.googleMyBusinessLocationId ||
              pendingGoogleLocation?.id,
            googleMyBusinessLocationName:
              serverSocialNetworks.googleMyBusinessLocationName ||
              pendingGoogleLocation?.locationName
          };
          const currentClient = onboardingClientRef.current;
          const refreshedClient = {
            ...currentClient,
            ...clientData,
            socialnetworks: {
              ...(currentClient.socialnetworks || {}),
              ...serverSocialNetworks,
              ...googleNetworks
            },
            clientSocialNetworks: {
              ...(currentClient.clientSocialNetworks || {}),
              ...serverSocialNetworks,
              ...googleNetworks
            }
          };

          if (!active) return;
          setServerGoogleClient({
            id: clientId,
            socialnetworks: refreshedClient.socialnetworks,
            clientSocialNetworks: refreshedClient.clientSocialNetworks
          });
          setGoogleConnected(true);
          setConnected((current) => ({ ...current, google: true }));
          setGoogleConnectionCheckStatus('connected');
          dispatch(setSelectedClient(refreshedClient));
          return;
        }
      } catch (error) {
        console.warn('Could not verify Google Business connection yet:', error);
      }

      if (!active) return;
      attempts += 1;
      if (attempts < maxAttempts) {
        timeoutId = window.setTimeout(checkConnection, retryDelay);
      } else if (active) {
        setGoogleConnectionCheckStatus('unconfirmed');
      }
    };

    checkConnection();
    return () => {
      active = false;
      if (timeoutId) window.clearTimeout(timeoutId);
    };
  }, [
    dispatch,
    googleConnectionCheckRetry,
    pendingGoogleLocation,
    selectedClientId,
    shouldVerifyGoogleConnection
  ]);

  useEffect(() => {
    if (!logoFile) {
      setLogoPreviewUrl('');
      return undefined;
    }

    const previewUrl = URL.createObjectURL(logoFile);
    setLogoPreviewUrl(previewUrl);
    return () => URL.revokeObjectURL(previewUrl);
  }, [logoFile]);

  useEffect(
    () => () => {
      if (googlePopupCloseTimeoutRef.current) {
        window.clearTimeout(googlePopupCloseTimeoutRef.current);
      }
    },
    []
  );

  useEffect(() => {
    if (!finished || celebratedRef.current) return;
    celebratedRef.current = true;
    const css = getComputedStyle(document.documentElement);
    const brand = (lightness) => {
      const [h, s] = css.getPropertyValue('--primary').trim().split(/\s+/);
      return `hsl(${h} ${s} ${lightness})`;
    };
    const colors = [
      brand('44%'),
      brand('58%'),
      brand('72%'),
      '#F2B705',
      '#EF6351',
      '#2FB8A6',
      '#7C6BF2',
      '#F2A6C4'
    ];
    confetti({
      particleCount: 60,
      spread: 55,
      startVelocity: 22,
      gravity: 0.9,
      ticks: 150,
      scalar: 0.8,
      zIndex: 60,
      origin: { x: 0.5, y: 0.32 },
      colors,
      disableForReducedMotion: true
    });
    const soft = window.setTimeout(() => {
      confetti({
        particleCount: 28,
        spread: 40,
        startVelocity: 18,
        ticks: 130,
        scalar: 0.7,
        zIndex: 60,
        origin: { x: 0.5, y: 0.28 },
        colors,
        disableForReducedMotion: true
      });
    }, 260);
    return () => window.clearTimeout(soft);
  }, [finished]);

  const visible = useMemo(
    () => QUESTIONS.filter((q) => !q.show || q.show(values, connected)),
    [values, connected]
  );
  const websiteOnboardingRequired =
    !clientRefreshLoading &&
    !clientRefreshError &&
    selectedClient?.websiteOnboardingRequired === true;
  const total = visible.length;
  const isWebsite = websiteOnboardingRequired && step >= total;
  const isLastRegularQuestion = !isWebsite && step === total - 1;
  const q = visible[Math.min(step, total - 1)];
  const progress =
    finished ||
    isWebsite ||
    (!websiteOnboardingRequired && isLastRegularQuestion)
      ? 100
      : Math.round((step / total) * 100);
  const counterLabel = isWebsite
    ? 'Final step'
    : `Question ${step + 1} of ${total}`;
  const maxNavigableStep = websiteOnboardingRequired ? total : total - 1;
  const v = values[q.id];
  const set = (val) => setValues((p) => ({ ...p, [q.id]: val }));
  const setBusinessType = (businessType) => {
    const categoryChanged =
      businessType.type !== values.businessType?.type ||
      businessType.choice !== values.businessType?.choice;
    const hasHomeServicesCategory =
      businessType.type === HOME_SERVICES_TYPE && Boolean(businessType.choice);
    const gbpOffers =
      hasHomeServicesCategory && !categoryChanged
        ? values.gbpOffers
        : createGbpOffersValue(
            hasHomeServicesCategory ? businessType.choice : ''
          );

    setValues((previous) => ({
      ...previous,
      businessType,
      gbpOffers
    }));
  };
  useEffect(() => {
    if (!websiteOnboardingRequired && step >= total) {
      setStep(Math.max(0, total - 1));
    }
  }, [step, total, websiteOnboardingRequired]);

  const hasWebsite = !noWebsite(values);
  const canContinueFirstStep =
    !googleProfileLoading &&
    canContinueAfterGoogleConnection(
      googleConnected,
      googleLocations,
      selectedGoogleLocationKey
    );

  const getZohoAccountId = () =>
    selectedClient?.socialnetworks?.zohoId ||
    selectedClient?.clientSocialNetworks?.zohoId ||
    onboardingClientRef.current?.socialnetworks?.zohoId ||
    onboardingClientRef.current?.clientSocialNetworks?.zohoId;

  const getWebsiteNoteRequest = () => {
    return buildZohoOnboardingRequest({
      zohoAccountId: getZohoAccountId(),
      values,
      web,
      hasExistingWebsite: !noWebsite(values)
    });
  };
  const refreshFacebookConnection = useCallback(async () => {
    if (!selectedClientId) return false;
    try {
      const response = await axios.get(`${API_CLIENTS}/${selectedClientId}`, {
        headers: { 'X-API-KEY': API_KEY },
        timeout: 10000
      });
      const clientData = normalizeClientResponse(response.data);
      const networks = clientData.socialnetworks || {};
      if (networks.facebook) {
        setConnected((current) => ({ ...current, facebook: true }));
        onboardingClientRef.current = {
          ...onboardingClientRef.current,
          socialnetworks: {
            ...(onboardingClientRef.current.socialnetworks || {}),
            ...networks
          }
        };
        return true;
      }
    } catch (error) {
      console.warn('Could not verify Facebook connection:', error);
    }
    return false;
  }, [selectedClientId]);

  const handleFacebookConnect = () => {
    if (!selectedClient?.id) return;
    const popup = initiateFacebookOAuth(selectedClient.id);
    if (!popup) return;
    window.clearInterval(facebookPopupWatcherRef.current);
    facebookPopupWatcherRef.current = window.setInterval(() => {
      if (!popup.closed) return;
      window.clearInterval(facebookPopupWatcherRef.current);
      facebookPopupWatcherRef.current = null;
      refreshFacebookConnection();
    }, 700);
  };

  useEffect(() => {
    refreshFacebookConnection();
  }, [refreshFacebookConnection]);

  useEffect(() => {
    const handleFacebookMessage = (event) => {
      if (event.origin !== window.location.origin) return;
      if (event.data?.type !== 'FACEBOOK_OAUTH_SUCCESS') return;
      setConnected((current) => ({ ...current, facebook: true }));
      refreshFacebookConnection();
    };

    window.addEventListener('message', handleFacebookMessage);
    return () => {
      window.removeEventListener('message', handleFacebookMessage);
      window.clearInterval(facebookPopupWatcherRef.current);
    };
  }, [refreshFacebookConnection]);

  useEffect(() => {
    const handleGoogleOAuthResult = async (event) => {
      if (googleOAuthResultHandledRef.current) return;

      const isStorageResult =
        event.type === 'storage' &&
        event.key === ONBOARDING_OAUTH_RESULT_KEY &&
        event.newValue;

      if (!isStorageResult) {
        if (
          event.source !== googlePopupRef.current ||
          (event.origin !== window.location.origin &&
            event.origin !== googlePopupOriginRef.current)
        ) {
          return;
        }
      }

      let message = event.data;
      if (isStorageResult) {
        try {
          message = JSON.parse(event.newValue);
        } catch (error) {
          console.error(
            'Could not read Google onboarding OAuth result:',
            error
          );
          setGoogleError(
            'Google connected, but its result could not be read. Please try again.'
          );
          return;
        }
      }

      if (!message) {
        return;
      }

      const isLegacyGoogleSuccess =
        message?.type === 'GOOGLE_OAUTH_SUCCESS' &&
        message.networkName === 'Google Business';
      const isOnboardingLocationResult =
        message?.type === 'GOOGLE_ONBOARDING_OAUTH_RESULT' &&
        Object.prototype.hasOwnProperty.call(message, 'location');
      if (
        !isLegacyGoogleSuccess &&
        !isOnboardingLocationResult &&
        message?.type !== 'GOOGLE_ONBOARDING_OAUTH_RESULT'
      ) {
        return;
      }
      googleOAuthResultHandledRef.current = true;

      if (googlePopupCloseTimeoutRef.current) {
        window.clearTimeout(googlePopupCloseTimeoutRef.current);
        googlePopupCloseTimeoutRef.current = null;
      }
      googlePopupRef.current = null;
      googlePopupOriginRef.current = null;
      setGoogleConnecting(false);

      if (isLegacyGoogleSuccess || isOnboardingLocationResult) {
        const location = message.location;
        setGoogleConnected(true);
        setConnected((current) => ({ ...current, google: true }));
        if (location) {
          setGoogleLocations([location]);
          setSelectedGoogleLocationKey(getGoogleLocationKey(location, 0));
          applyGoogleLocation(location);
        }
        setGoogleError('');
        setGoogleProfileLoading(true);
        try {
          const profileInfo = await getGoogleProfileInfo(
            message.clientId || selectedClient?.id
          );
          const profile = profileInfo.data?.data || profileInfo.data;
          const profileAnswers = mapGoogleLocationToAnswers(profile);
          if (
            Object.keys(profileAnswers).length > 0 ||
            getGoogleLogoSource(profile)
          ) {
            const profileLocation = {
              ...profile,
              ...location
            };
            setGoogleLocations([profileLocation]);
            setSelectedGoogleLocationKey(
              getGoogleLocationKey(profileLocation, 0)
            );
            applyGoogleLocation(profileLocation);
          } else if (!location) {
            setGoogleError(
              'Google connected, but profile details could not be loaded. You can continue and fill in the details manually.'
            );
          }
        } catch (error) {
          console.error('Error loading Google profile for onboarding:', error);
          if (!location) {
            setGoogleError(
              'Google connected, but profile details could not be loaded. You can continue and fill in the details manually.'
            );
          }
        } finally {
          setGoogleProfileLoading(false);
        }
        return;
      }

      if (!message.success) {
        setGoogleError(
          message.error ||
            'Could not connect Google Business Profile. Please try again.'
        );
        return;
      }

      const locations = message.locations;
      if (!Array.isArray(locations)) {
        setGoogleError('Google connected but did not return a locations list.');
        return;
      }

      setGoogleLocations(locations);
      setGoogleConnected(true);
      setConnected((current) => ({ ...current, google: true }));
      setSelectedGoogleLocationKey('');
      setGoogleError('');
    };

    window.addEventListener('message', handleGoogleOAuthResult);
    window.addEventListener('storage', handleGoogleOAuthResult);
    return () => {
      window.removeEventListener('message', handleGoogleOAuthResult);
      window.removeEventListener('storage', handleGoogleOAuthResult);
    };
  }, [applyGoogleLocation, selectedClient?.id]);

  const handleGoogleConnect = () => {
    const clientId = selectedClient?.id;
    const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    const redirectUri = getGoogleOAuthRedirectUri();
    if (!clientId) {
      setGoogleError(
        'Select a client before connecting Google Business Profile.'
      );
      return;
    }
    if (!googleClientId || !redirectUri) {
      setGoogleError(
        'Google Business connection is not configured. Contact support.'
      );
      return;
    }

    try {
      googlePopupOriginRef.current = new URL(redirectUri).origin;
    } catch (error) {
      console.error('Invalid Google OAuth redirect URL:', error);
      setGoogleError(
        'Google Business connection has an invalid redirect URL. Contact support.'
      );
      return;
    }

    const state = encodeURIComponent(
      JSON.stringify({
        clientId,
        platform: 'adlersuite',
        openerOrigin: window.location.origin,
        onboarding: true
      })
    );
    const popup = initiateGoogleOAuth(googleClientId, redirectUri, state);
    if (!popup) {
      googlePopupOriginRef.current = null;
      setGoogleError(
        'Unable to open Google OAuth. Please allow popups and try again.'
      );
      return;
    }

    if (googlePopupCloseTimeoutRef.current) {
      window.clearTimeout(googlePopupCloseTimeoutRef.current);
      googlePopupCloseTimeoutRef.current = null;
    }
    setGoogleError('');
    setGoogleConnecting(true);
    googleOAuthResultHandledRef.current = false;
    googlePopupRef.current = popup;
    googlePopupCloseTimeoutRef.current = window.setTimeout(() => {
      if (googleOAuthResultHandledRef.current) return;
      googlePopupRef.current = null;
      googlePopupOriginRef.current = null;
      googlePopupCloseTimeoutRef.current = null;
      setGoogleConnecting(false);
      setGoogleError(
        'Google connection timed out or could not be completed. Please try again.'
      );
    }, 180000);
  };

  const handleGoogleLocationChange = (locationKey) => {
    setSelectedGoogleLocationKey(locationKey);
    const selectedLocation = googleLocations.find(
      (location, index) => getGoogleLocationKey(location, index) === locationKey
    );
    if (selectedLocation) applyGoogleLocation(selectedLocation);
  };

  const handleContinue = async () => {
    if (websiteNoteInFlightRef.current) return;
    if (isWebsite && websiteNoteOutcomeRef.current === 'unknown') {
      setSaveError(
        'The Zoho note delivery result is unknown. Verify the note in Zoho before retrying.'
      );
      return;
    }

    if (isWebsite) websiteNoteInFlightRef.current = true;

    setSaveError('');
    setIsSaving(true);

    try {
      if (!selectedClient?.id) {
        throw new Error('Select a client before saving onboarding answers.');
      }
      if (!API_NEWCLIENT) {
        throw new Error('The client update service is not configured.');
      }
      const websiteNoteRequest = isWebsite ? getWebsiteNoteRequest() : null;

      let answerUpdate = {};
      if (isWebsite) {
        answerUpdate = {
          ...buildOnboardingStepUpdate(
            'websiteContent',
            values,
            onboardingClientRef.current,
            web
          ),
          ...buildOnboardingStepUpdate(
            'websiteColors',
            values,
            onboardingClientRef.current,
            web
          ),
          ...buildOnboardingStepUpdate(
            'websiteLayout',
            values,
            onboardingClientRef.current,
            web
          )
        };
      } else if (q.id === 'logo') {
        const logoFile = values.logo?.[0];
        if (logoFile) {
          answerUpdate.logoUrl = await uploadLogoToS3WithClientId(
            logoFile,
            selectedClient.id
          );
        }
      } else if (q.id === 'photos') {
        // Upload only files not yet saved, so going back/forth doesn't duplicate.
        for (const photo of values.photos ?? []) {
          if (uploadedPhotosRef.current.has(photo)) continue;
          await uploadPhotoToGallery(photo, selectedClient.id);
          uploadedPhotosRef.current.add(photo);
        }
      } else {
        answerUpdate = buildOnboardingStepUpdate(
          q.id,
          values,
          onboardingClientRef.current
        );
      }

      if (Object.keys(answerUpdate).length > 0) {
        const updatePayload = {
          id: selectedClient.id,
          ...answerUpdate
        };
        const response = await axios.post(API_NEWCLIENT, updatePayload);
        if (response.status !== 200) {
          throw new Error('The onboarding answer could not be saved.');
        }

        const { clientFlagsUpdate, ...savedAnswerUpdate } = answerUpdate;
        if (onboardingClientRef.current.id === updatePayload.id) {
          onboardingClientRef.current = {
            ...onboardingClientRef.current,
            ...savedAnswerUpdate,
            ...(clientFlagsUpdate && {
              clientFlags: applyClientFlagChanges(
                onboardingClientRef.current.clientFlags || [],
                clientFlagsUpdate
              )
            }),
            ...(answerUpdate.mergeFields && {
              mergeFields: {
                ...onboardingClientRef.current.mergeFields,
                ...answerUpdate.mergeFields
              }
            })
          };
        }
        if (q.id === 'name') {
          dispatch(
            updateSelectedClientName({
              id: updatePayload.id,
              clientName: answerUpdate.clientName
            })
          );
        }
      }

      if (isWebsite && websiteNoteOutcomeRef.current !== 'created') {
        const submissionData = await submitWebsitePreferences({
          endpoint: API_ZOHO_WEBSITE_NOTE,
          request: websiteNoteRequest
        });
        websiteCaseIdRef.current = submissionData.zohoCaseId || null;
        websiteNoteIdRef.current =
          submissionData.zohoTaskId || submissionData.zohoNoteId || null;
        websiteNoteOutcomeRef.current = 'created';
      }

      if (editingFromReview) {
        setEditingFromReview(false);
        setShowReview(true);
        setFinished(true);
      } else if (
        isWebsite ||
        (!websiteOnboardingRequired && isLastRegularQuestion)
      ) {
        setFinished(true);
      } else {
        setStep((currentStep) => currentStep + 1);
      }
    } catch (error) {
      console.error('Onboarding submission failed', {
        code: error.code,
        requestId: error.requestId,
        submissionOutcome: error.noteOutcome
      });
      if (isWebsite && error.noteOutcome) {
        websiteNoteOutcomeRef.current = error.noteOutcome;
      }
      setSaveError(
        error.response?.data?.message ||
          error.message ||
          'Could not save this answer or complete the Zoho submission. Please try again.'
      );
    } finally {
      websiteNoteInFlightRef.current = false;
      setIsSaving(false);
    }
  };

  const reviewSections = useMemo(() => {
    const map = new Map();
    visible.forEach((qq, idx) => {
      if (!map.has(qq.section)) map.set(qq.section, []);
      map.get(qq.section).push({
        title: qq.title,
        answer:
          qq.id === 'logo' && values.logoUrl
            ? 'Existing business logo'
            : formatAnswer(qq, values[qq.id], values),
        editStep: idx
      });
    });
    const webItems = [
      hasWebsite && { title: 'Website content', answer: web.content ?? null },
      {
        title: 'Website colors',
        answer:
          typeof web.colors === 'string' ? web.colors.trim() || null : null
      },
      { title: 'Website layout', answer: web.layout ?? null }
    ]
      .filter(Boolean)
      .map((i) => ({ ...i, editStep: visible.length }));
    if (websiteOnboardingRequired && webItems.some((i) => i.answer)) {
      map.set('Website', webItems);
    }
    return [...map.entries()];
  }, [visible, values, web, hasWebsite, websiteOnboardingRequired]);

  const renderInput = () => {
    switch (q.type) {
      case 'text':
      case 'phone':
      case 'email':
        return (
          <Input
            type={q.type === 'phone' ? 'tel' : q.type}
            value={v ?? ''}
            onChange={(e) => set(e.target.value)}
            placeholder={q.placeholder}
            className="h-14 text-base"
          />
        );
      case 'website':
        return (
          <div className="space-y-3">
            <Input
              type="text"
              inputMode="url"
              disabled={!!v?.none}
              value={v?.url ?? ''}
              onChange={(e) => set({ ...v, url: e.target.value })}
              placeholder={q.placeholder}
              className="h-14 text-base"
            />
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <Checkbox
                checked={!!v?.none}
                onCheckedChange={(c) => set({ ...v, none: !!c })}
              />
              I don't have a website
            </label>
          </div>
        );
      case 'address':
        return (
          <div className="space-y-3">
            <Input
              value={v?.street ?? ''}
              onChange={(e) => set({ ...v, street: e.target.value })}
              placeholder="123 Main St, Miami, FL 33101"
              className="h-14 text-base"
            />
            <Input
              value={v?.suite ?? ''}
              onChange={(e) => set({ ...v, suite: e.target.value })}
              placeholder="Suite / Unit (optional)"
              className="h-14 text-base"
            />
          </div>
        );
      case 'textarea':
        return (
          <Textarea
            value={v ?? ''}
            onChange={(e) => set(e.target.value)}
            placeholder={q.placeholder}
            className="min-h-[160px] text-base"
          />
        );
      case 'tags':
        return (
          <CreatableSelect
            isMulti
            isClearable={false}
            value={getTagValues(v)}
            onChange={(selectedOptions) =>
              set(
                selectedOptions
                  ? selectedOptions.map((option) => option.label).join('\n')
                  : ''
              )
            }
            options={[]}
            noOptionsMessage={() => null}
            formatCreateLabel={(inputValue) => `Add "${inputValue}"`}
            placeholder={q.placeholder}
            styles={onboardingTagStyles}
          />
        );
      case 'specialOffers':
        return (
          <div className="space-y-5">
            <Textarea
              value={v ?? ''}
              onChange={(event) => set(event.target.value)}
              className="min-h-[145px] text-base"
            />
            {values.businessType?.type === HOME_SERVICES_TYPE &&
              values.businessType?.choice && (
                <GbpOffersPicker
                  category={values.businessType.choice}
                  value={values.gbpOffers}
                  onChange={(gbpOffers) =>
                    setValues((previous) => ({ ...previous, gbpOffers }))
                  }
                />
              )}
          </div>
        );
      case 'file':
      case 'files': {
        const files = v ?? [];
        return (
          <div className="space-y-3">
            <label className="flex flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed p-8 cursor-pointer hover:bg-muted/50 transition-colors">
              <Upload className="h-8 w-8 text-muted-foreground" />
              <span className="text-sm font-medium">
                Click to upload {q.type === 'files' ? 'photos' : 'your logo'}
              </span>
              <span className="text-xs text-muted-foreground">
                {q.type === 'file'
                  ? 'PNG, JPG or SVG, under 2MB.'
                  : 'Multiple files allowed.'}
              </span>
              <input
                type="file"
                className="hidden"
                multiple={q.type === 'files'}
                accept={
                  q.type === 'file' ? '.png,.jpg,.jpeg,.svg' : '.png,.jpg,.jpeg'
                }
                onChange={(e) => {
                  const list = Array.from(e.target.files ?? []);
                  if (q.type === 'file' && list.length) {
                    logoRequestRef.current += 1;
                    setGoogleLogoError('');
                    setGoogleLogoLoading(false);
                  }
                  set(
                    q.type === 'files' ? [...files, ...list] : list.slice(0, 1)
                  );
                  e.target.value = '';
                }}
              />
            </label>
            {q.type === 'file' && googleLogoLoading && (
              <p
                role="status"
                className="flex items-center gap-2 text-sm font-medium text-blue-700">
                <Loader2 className="h-4 w-4 shrink-0 animate-spin" />
                Loading your Google Business Profile logo...
              </p>
            )}
            {q.type === 'file' && googleLogoError && (
              <p role="alert" className="text-sm font-medium text-red-700">
                {googleLogoError}
              </p>
            )}
            {q.type === 'files' && files.length > 0 && (
              <div className="grid grid-cols-3 gap-3 sm:grid-cols-4">
                {files.map((f, i) => (
                  <FileThumb
                    key={`${f.name}-${i}`}
                    file={f}
                    onRemove={() => set(files.filter((_, j) => j !== i))}
                  />
                ))}
              </div>
            )}
            {q.type === 'file' &&
              files.map((f, i) => (
                <div
                  key={i}
                  className="flex items-center justify-between rounded-xl border px-3 py-2 text-sm">
                  <div className="flex min-w-0 items-center gap-3">
                    {q.type === 'file' && logoPreviewUrl && (
                      <img
                        src={logoPreviewUrl}
                        alt="Business logo preview"
                        className="h-16 w-16 shrink-0 rounded-lg border bg-white p-1 object-contain"
                      />
                    )}
                    <span className="truncate">{f.name}</span>
                  </div>
                  <button
                    onClick={() => set(files.filter((_, j) => j !== i))}
                    aria-label="Remove file">
                    <X className="h-4 w-4 text-muted-foreground" />
                  </button>
                </div>
              ))}
            {q.type === 'file' && !files.length && values.logoUrl && (
              <div className="flex items-center gap-3 rounded-xl border px-3 py-2 text-sm">
                <img
                  src={values.logoUrl}
                  alt="Saved business logo"
                  className="h-16 w-16 shrink-0 rounded-lg border bg-white p-1 object-contain"
                />
                <span className="truncate">Current business logo</span>
              </div>
            )}
          </div>
        );
      }
      case 'radio': {
        const choice = typeof v === 'object' ? v?.choice ?? '' : v ?? '';
        const extra = typeof v === 'object' ? v?.extra ?? '' : '';
        const wrap = !!q.followUp;
        return (
          <div className="space-y-4">
            <RadioGroup
              value={choice}
              onValueChange={(o) => set(wrap ? { choice: o, extra } : o)}
              className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {q.options.map((o) => (
                <label key={o} className={choiceCls}>
                  <RadioGroupItem value={o} />
                  <span className="text-sm">{o}</span>
                </label>
              ))}
            </RadioGroup>
            {q.followUp && choice && q.followUp.when(choice) && (
              <div className="space-y-2">
                <label className="text-sm font-medium">
                  {q.followUp.label}
                </label>
                <Input
                  value={extra}
                  onChange={(e) => set({ choice, extra: e.target.value })}
                  className="h-14 text-base"
                />
              </div>
            )}
            {q.note && choice && q.note.when(choice) && (
              <p className="flex items-start gap-2 rounded-xl bg-primary/5 p-3 text-sm text-muted-foreground">
                <Info className="h-4 w-4 mt-0.5 shrink-0 text-primary" />
                {q.note.text}
              </p>
            )}
          </div>
        );
      }
      case 'businessType': {
        const categoryOptions = getBusinessCategoryOptions(v?.type);
        return (
          <div className="space-y-5">
            <RadioGroup
              value={v?.type ?? ''}
              onValueChange={(type) => {
                const categoryExists = getBusinessCategoryOptions(
                  type
                ).includes(v?.choice);
                setBusinessType({
                  type,
                  choice: categoryExists ? v?.choice : '',
                  other: categoryExists ? v?.other || '' : ''
                });
              }}
              className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {TYPE_OPTIONS.map((type) => (
                <label key={type} className={choiceCls}>
                  <RadioGroupItem value={type} />
                  <span className="text-sm">{type}</span>
                </label>
              ))}
            </RadioGroup>
            {v?.type && (
              <div className="space-y-3">
                <Select
                  value={v?.choice ?? ''}
                  onValueChange={(choice) =>
                    setBusinessType({
                      ...v,
                      choice,
                      other: choice === OTHER_CATEGORY ? v?.other || '' : ''
                    })
                  }>
                  <SelectTrigger className="h-14 text-base">
                    <SelectValue placeholder="Select your business category" />
                  </SelectTrigger>
                  <SelectContent>
                    {categoryOptions.map((category) => (
                      <SelectItem key={category} value={category}>
                        {category}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-sm text-muted-foreground">
                  Pick the one that matches most of your work. This becomes your
                  main category on Google.
                </p>
                {v?.choice === OTHER_CATEGORY && (
                  <Input
                    value={v?.other ?? ''}
                    onChange={(e) => set({ ...v, other: e.target.value })}
                    placeholder="Tell us what you do (e.g., Mobile auto detailing)"
                    className="h-14 text-base"
                  />
                )}
              </div>
            )}
          </div>
        );
      }
      case 'checkboxes': {
        const list = v ?? [];
        const toggle = (o) => {
          if (list.includes(o)) return set(list.filter((x) => x !== o));
          if (q.exclusive && o === q.exclusive) return set([o]);
          const next = [...list.filter((x) => x !== q.exclusive), o];
          if (q.max && next.length > q.max) return;
          set(next);
        };
        const full = !!q.max && list.length >= q.max;
        return (
          <div
            className={`grid grid-cols-1 gap-3 ${
              q.max ? '' : 'sm:grid-cols-2'
            }`}>
            {q.options.map((o) => {
              const disabled = full && !list.includes(o);
              return (
                <label
                  key={o}
                  className={`${choiceCls} p-3 ${
                    disabled ? 'opacity-50 cursor-not-allowed' : ''
                  }`}>
                  <Checkbox
                    checked={list.includes(o)}
                    disabled={disabled}
                    onCheckedChange={() => toggle(o)}
                  />
                  <span className="text-sm">{o}</span>
                </label>
              );
            })}
          </div>
        );
      }
      case 'holidays': {
        const list = v ?? [];
        const none = list.includes('None');
        const toggle = (o) =>
          set(
            list.includes(o)
              ? list.filter((x) => x !== o)
              : [...list.filter((x) => x !== 'None'), o]
          );
        const group = (label, items) => (
          <div className="space-y-2">
            <p className="text-sm font-medium">{label}</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {items.map((o) => (
                <label key={o} className={`${choiceCls} p-3`}>
                  <Checkbox
                    checked={list.includes(o)}
                    onCheckedChange={() => toggle(o)}
                  />
                  <span className="text-sm">{o}</span>
                </label>
              ))}
            </div>
          </div>
        );
        return (
          <div className="space-y-5">
            <div className="flex flex-wrap gap-3">
              <Button
                type="button"
                variant="outline"
                className="rounded-full"
                onClick={() => set([...ALL_HOLIDAYS])}>
                Select all
              </Button>
              <label className="flex items-center gap-2 rounded-full border px-4 text-sm cursor-pointer">
                <Checkbox
                  checked={none}
                  onCheckedChange={(c) => set(c ? ['None'] : [])}
                />
                None
              </label>
            </div>
            {group('United States', US_HOLIDAYS)}
            {group('Canada', CA_HOLIDAYS)}
          </div>
        );
      }
      case 'hours': {
        const hours = v ?? defaultHours();
        return (
          <div className="space-y-6">
            <BusinessHoursSelect
              value={hours.days}
              onChange={(days) => set({ ...hours, days })}
              allow24
            />
            <div className="space-y-3">
              <p className="text-sm font-medium">
                Do you offer emergency service?
              </p>
              <RadioGroup
                value={hours.emergency ?? ''}
                onValueChange={(o) => set({ ...hours, emergency: o })}
                className="grid grid-cols-2 gap-3">
                {['Yes', 'No'].map((o) => (
                  <label key={o} className={choiceCls}>
                    <RadioGroupItem value={o} />
                    <span className="text-sm">{o}</span>
                  </label>
                ))}
              </RadioGroup>
            </div>
          </div>
        );
      }
      case 'fields': {
        const data = v ?? {};
        const vals = data.vals ?? [];
        const none = data.none ?? [];
        return (
          <div className="space-y-4">
            {q.fields.map((f, i) => (
              <div key={f.label} className="space-y-2">
                <label className="text-sm font-medium">{f.label}</label>
                <Input
                  type={f.password ? 'password' : 'text'}
                  autoComplete={f.password ? 'new-password' : 'off'}
                  disabled={!!data.unsure || !!none[i]}
                  value={vals[i] ?? ''}
                  placeholder={f.placeholder}
                  onChange={(e) => {
                    const n = [...vals];
                    n[i] = e.target.value;
                    set({ ...data, vals: n });
                  }}
                  className="h-14 text-base"
                />
                {f.noneLabel && (
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <Checkbox
                      checked={!!none[i]}
                      onCheckedChange={(c) => {
                        const n = [...none];
                        n[i] = !!c;
                        set({ ...data, none: n });
                      }}
                    />
                    {f.noneLabel}
                  </label>
                )}
              </div>
            ))}
            {q.unsureLabel && (
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <Checkbox
                  checked={!!data.unsure}
                  onCheckedChange={(c) => set({ ...data, unsure: !!c })}
                />
                {q.unsureLabel}
              </label>
            )}
          </div>
        );
      }
      default:
        return null;
    }
  };

  if (clientRefreshLoading || clientRefreshError) {
    return (
      <div className="min-h-screen bg-muted/40 flex items-center justify-center px-4">
        <div className="w-full max-w-md rounded-3xl border bg-background p-8 text-center shadow-sm">
          <h1 className="text-xl font-bold">
            {clientRefreshLoading
              ? 'Loading client information...'
              : 'Unable to load client information'}
          </h1>
          {clientRefreshError && (
            <p role="alert" className="mt-3 text-sm text-destructive">
              {clientRefreshError}
            </p>
          )}
          {clientRefreshError && (
            <Button
              className="mt-6 rounded-full"
              onClick={() => setClientRefreshAttempt((attempt) => attempt + 1)}>
              Retry
            </Button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-muted/40 flex flex-col">
      <header className="bg-background border-b">
        <div className="mx-auto w-full max-w-2xl px-4 py-4 flex items-center justify-between gap-3">
          <img src={logo} alt="Adler Local" className="h-8" />
          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="icon"
              className="h-9 w-9 rounded-full"
              onClick={() => setStep((s) => Math.max(0, s - 1))}
              disabled={finished || step === 0}
              aria-label="Go to previous question"
              title="Previous question">
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              className="h-9 w-9 rounded-full"
              onClick={() => setStep((s) => Math.min(maxNavigableStep, s + 1))}
              disabled={
                finished ||
                isWebsite ||
                (isLastRegularQuestion && !websiteOnboardingRequired) ||
                (step === 0 && !canContinueFirstStep) ||
                (q.id === 'logo' && googleLogoLoading)
              }
              aria-label="Go to next question"
              title="Next question">
              <ArrowRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
        <Progress value={progress} className="h-1 rounded-none" />
      </header>

      <main className="flex-1 flex items-start justify-center px-4 pt-3 pb-10 md:pt-4 md:pb-16">
        <div className="w-full max-w-2xl space-y-4">
          {(selectedClient?.clientName || !finished) && (
            <div className="flex min-w-0 items-center justify-between gap-3">
              {selectedClient?.clientName ? (
                <p className="min-w-0 truncate text-sm font-medium text-foreground">
                  {selectedClient.clientName}
                </p>
              ) : (
                <span />
              )}
              {!finished && (
                <span className="shrink-0 rounded-full border bg-background px-3 py-1 text-xs font-medium tabular-nums text-muted-foreground shadow-sm">
                  {counterLabel}
                </span>
              )}
            </div>
          )}
          {!finished && step === 0 && (
            <div className="bg-background rounded-3xl border shadow-sm p-5 md:p-6">
              <h2 className="text-lg md:text-xl font-bold mb-3">
                Welcome to Adler Local! Let's get you set up.
              </h2>
              <p className="text-sm text-muted-foreground mb-3">
                To get your business found on Google, we need a few details.
                Start by connecting your Google Business Profile — your listing
                on Google Search and Maps. We need it to start your service.
                Once it's connected, we'll fill in most of this form for you.
              </p>
              <p className="text-sm text-muted-foreground">
                Then connect your business Facebook page so we can post for you.
              </p>
              <div className="grid grid-cols-1 gap-3 mt-5">
                <Button
                  type="button"
                  className="h-14 w-full rounded-full"
                  onClick={handleGoogleConnect}
                  disabled={
                    googleConnecting || googleConnected || !selectedClient?.id
                  }>
                  {googleConnecting || googleProfileLoading ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : googleConnected ? (
                    <Check className="h-4 w-4 mr-2 shrink-0" />
                  ) : (
                    <LogIn className="h-4 w-4 mr-2 shrink-0" />
                  )}
                  <span className="truncate">
                    {googleConnecting
                      ? 'Connecting Google Business Profile...'
                      : googleConnected
                      ? 'Google Business Profile connected'
                      : 'Connect Google Business Profile'}
                  </span>
                </Button>
                {!selectedClient?.id && (
                  <p className="text-sm text-destructive">
                    Select a client in Adler Suite before connecting Google
                    Business Profile.
                  </p>
                )}
                <Button
                  type="button"
                  className="h-14 w-full rounded-full"
                  onClick={handleFacebookConnect}
                  disabled={!selectedClient?.id || connected.facebook}>
                  {connected.facebook ? (
                    <Check className="h-4 w-4 mr-2 shrink-0" />
                  ) : (
                    <LogIn className="h-4 w-4 mr-2 shrink-0" />
                  )}
                  <span className="truncate">
                    {connected.facebook
                      ? 'Facebook connected'
                      : 'Connect Facebook'}
                  </span>
                </Button>
              </div>

              {googleConnected && googleLocations.length > 1 && (
                <div className="space-y-2 mt-4">
                  <label
                    htmlFor="google-business-location"
                    className="text-sm font-medium">
                    Choose your Google Business location
                  </label>
                  <Select
                    value={selectedGoogleLocationKey}
                    onValueChange={handleGoogleLocationChange}>
                    <SelectTrigger
                      id="google-business-location"
                      className="h-12">
                      <SelectValue placeholder="Select a business location" />
                    </SelectTrigger>
                    <SelectContent>
                      {googleLocations.map((location, index) => {
                        const locationKey = getGoogleLocationKey(
                          location,
                          index
                        );
                        return (
                          <SelectItem key={locationKey} value={locationKey}>
                            {getGoogleLocationLabel(location)}
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {googleError && (
                <p
                  role="alert"
                  className="flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm font-medium text-red-700 mt-4">
                  <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                  {googleError}
                </p>
              )}

              {shouldVerifyGoogleConnection &&
                !googleConnected &&
                googleConnectionCheckStatus === 'checking' && (
                  <p
                    role="status"
                    className="flex items-center gap-2 rounded-xl border border-blue-200 bg-blue-50 px-3 py-2 text-sm font-medium text-blue-700 mt-4">
                    <Loader2 className="h-4 w-4 shrink-0 animate-spin" />
                    Checking whether Google Business finished connecting...
                  </p>
                )}

              {shouldVerifyGoogleConnection &&
                !googleConnected &&
                googleConnectionCheckStatus === 'unconfirmed' && (
                  <div
                    role="status"
                    className="flex flex-col items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800 mt-4">
                    <p>
                      Google Business is not showing as connected yet. You can
                      check again in a moment.
                    </p>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        setGoogleConnectionCheckRetry((value) => value + 1)
                      }>
                      Check connection again
                    </Button>
                  </div>
                )}

              {googleConnected && (
                <p
                  role="status"
                  className={`flex items-start gap-2 rounded-xl border px-3 py-2 text-sm font-medium mt-4 ${
                    googleProfileLoading
                      ? 'border-blue-200 bg-blue-50 text-blue-700'
                      : googleLocations.length === 0 ||
                        (googleLocations.length > 1 &&
                          !selectedGoogleLocationKey)
                      ? 'border-amber-200 bg-amber-50 text-amber-800'
                      : 'border-green-200 bg-green-50 text-green-700'
                  }`}>
                  {googleProfileLoading ? (
                    <Loader2 className="h-4 w-4 mt-0.5 shrink-0 animate-spin" />
                  ) : (
                    <Check className="h-4 w-4 mt-0.5 shrink-0" />
                  )}
                  {googleProfileLoading
                    ? 'Google connected. Loading your Business Profile details...'
                    : googleLocations.length === 0
                    ? 'Google connected. No Business Profile locations were returned; you can continue and fill in the details manually.'
                    : googleLocations.length > 1 && !selectedGoogleLocationKey
                    ? 'Google connected. Choose a location to prefill the onboarding form.'
                    : 'Google connected. We filled in the available business details; review and edit them as you continue.'}
                </p>
              )}

              <p className="text-sm text-muted-foreground mt-4 pt-4 border-t">
                <span className="font-semibold text-foreground">Stuck?</span>{' '}
                Call us at{' '}
                <a
                  href="tel:8888846050"
                  className="text-primary font-medium hover:underline">
                  888-884-6050
                </a>{' '}
                and we'll walk you through it.
              </p>
            </div>
          )}
          {finished ? (
            <div className="bg-background rounded-3xl border p-8 md:p-12 shadow-sm text-center">
              <div className="mx-auto h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center mb-6">
                <Check className="h-8 w-8 text-primary" />
              </div>
              <h1 className="text-2xl md:text-3xl font-bold mb-3">
                You're all set!
              </h1>
              <p className="text-muted-foreground mb-6">
                Thanks — your answers are in. We'll use them to shape your
                strategy.
              </p>
              <div className="flex justify-center mb-8">
                <Button
                  variant="outline"
                  className="h-14 w-full sm:w-auto sm:px-10 rounded-full"
                  onClick={() => setShowReview((s) => !s)}>
                  <ClipboardList className="h-4 w-4 mr-2" />
                  {showReview ? 'Hide my answers' : 'Review my answers'}
                  {showReview ? (
                    <ChevronUp className="h-4 w-4 ml-2" />
                  ) : (
                    <ChevronDown className="h-4 w-4 ml-2" />
                  )}
                </Button>
              </div>
              {showReview && (
                <div className="text-left rounded-2xl border bg-muted/40 p-5 mb-8 max-h-[420px] overflow-y-auto space-y-6">
                  {reviewSections.map(([section, items]) => (
                    <div key={section}>
                      <p className="text-xs font-semibold uppercase tracking-wide text-primary mb-2">
                        {section}
                      </p>
                      <div className="space-y-2">
                        {items.map((item) => (
                          <div
                            key={item.title}
                            className="flex items-start justify-between gap-3 text-sm">
                            <div className="min-w-0">
                              <p className="text-muted-foreground">
                                {item.title}
                              </p>
                              <p className="font-medium whitespace-pre-line">
                                {item.answer ?? (
                                  <span className="italic text-muted-foreground">
                                    No answer
                                  </span>
                                )}
                              </p>
                            </div>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 shrink-0 rounded-full"
                              aria-label={`Edit: ${item.title}`}
                              title="Edit this answer"
                              onClick={() => {
                                setShowReview(false);
                                setEditingFromReview(true);
                                setFinished(false);
                                setStep(item.editStep);
                              }}>
                              <Pencil className="h-4 w-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <Button
                className="h-14 w-full sm:w-auto sm:px-10 rounded-full"
                onClick={() => history.push('/client')}>
                Go to Dashboard
              </Button>
            </div>
          ) : isWebsite ? (
            <div className="bg-background rounded-3xl border p-6 md:p-10 shadow-sm space-y-10">
              <p className="text-xs font-semibold uppercase tracking-wide text-primary">
                Website
              </p>

              {hasWebsite && (
                <div className="space-y-3">
                  <h2 className="text-xl md:text-2xl font-bold">
                    Website content
                  </h2>
                  <p className="text-muted-foreground">
                    Do you want to keep your current website's wording, or
                    should we write new content?
                  </p>
                  <RadioGroup
                    value={web.content ?? ''}
                    onValueChange={(o) => setWeb((w) => ({ ...w, content: o }))}
                    className="grid grid-cols-1 gap-3">
                    {WEBSITE_CONTENT_OPTIONS.map((o) => (
                      <label key={o} className={choiceCls}>
                        <RadioGroupItem value={o} />
                        <span className="text-sm">{o}</span>
                      </label>
                    ))}
                  </RadioGroup>
                </div>
              )}

              <div className="space-y-3">
                <h2 className="text-xl md:text-2xl font-bold">Colors</h2>
                <p className="text-muted-foreground">
                  What colors do you want for your website? List your brand
                  colors, or leave blank and we'll match your logo.
                </p>
                <Input
                  value={web.colors ?? ''}
                  onChange={(event) => {
                    const colors = event.target.value;
                    setWeb((currentWeb) => ({ ...currentWeb, colors }));
                  }}
                  placeholder="e.g. Navy blue and orange"
                  className="h-14 text-base"
                />
              </div>

              <div className="space-y-3">
                <h2 className="text-xl md:text-2xl font-bold">
                  Website layout
                </h2>
                <p className="text-muted-foreground">
                  Which layout would you like for your website? These layouts
                  are proven to turn the most website visitors into calls and
                  new customers.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {LAYOUTS.map((l) => {
                    const selected = web.layout === l.name;
                    return (
                      <div
                        key={l.name}
                        className={`group rounded-2xl border p-2 cursor-pointer transition-all hover:shadow-md ${
                          selected ? 'border-primary ring-2 ring-primary' : ''
                        }`}
                        onClick={() =>
                          setWeb((w) => ({ ...w, layout: l.name }))
                        }
                        role="button"
                        aria-pressed={selected}>
                        <div className="relative overflow-hidden rounded-xl">
                          <img
                            src={l.img}
                            alt={`${l.name} website layout`}
                            className="w-full object-cover object-top aspect-[4/3]"
                          />
                          {selected && (
                            <span className="absolute right-2 top-2 flex h-6 w-6 items-center justify-center rounded-full bg-primary shadow-md">
                              <Check className="h-3.5 w-3.5 text-primary-foreground" />
                            </span>
                          )}
                        </div>
                        <div className="flex items-center justify-between gap-2 px-2 pt-3 pb-1">
                          <span className="whitespace-nowrap text-sm font-semibold">
                            {l.name}
                          </span>
                          <a
                            href={l.url}
                            target="_blank"
                            rel="noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="inline-flex h-6 shrink-0 items-center gap-1 rounded-full border border-primary/30 px-2 text-[11px] font-medium text-primary transition-colors hover:bg-primary hover:text-primary-foreground">
                            Preview
                            <ExternalLink className="h-3 w-3" />
                          </a>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {saveError && (
                <p role="alert" className="text-sm text-destructive">
                  {saveError}
                </p>
              )}

              <div className="flex flex-col sm:flex-row gap-3 pt-2">
                <Button
                  variant="outline"
                  className="h-14 w-full sm:w-auto px-8 has-[>svg]:px-8 rounded-full"
                  disabled={isSaving}
                  onClick={() => {
                    if (editingFromReview) {
                      setEditingFromReview(false);
                      setShowReview(true);
                      setFinished(true);
                    } else {
                      setStep(total - 1);
                    }
                  }}>
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  {editingFromReview ? 'Back to review' : 'Back'}
                </Button>
                <Button
                  className="h-14 w-full sm:flex-1 rounded-full"
                  disabled={isSaving}
                  onClick={handleContinue}>
                  {isSaving ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : null}
                  {isSaving ? 'Saving...' : 'Submit'}
                  {!isSaving && <ArrowRight className="h-4 w-4 ml-2" />}
                </Button>
              </div>
            </div>
          ) : (
            <div className="bg-background rounded-3xl border p-6 md:p-10 shadow-sm">
              <p className="text-xs font-semibold uppercase tracking-wide text-primary mb-3">
                {q.section}
              </p>
              <h1 className="text-xl md:text-2xl font-bold mb-2">{q.title}</h1>
              {q.description ? (
                <p className="text-muted-foreground mb-6 whitespace-pre-line">
                  {q.description}
                </p>
              ) : (
                <div className="mb-6" />
              )}

              {renderInput()}

              {saveError && (
                <p role="alert" className="mt-4 text-sm text-destructive">
                  {saveError}
                </p>
              )}

              <div className="flex flex-col sm:flex-row gap-3 mt-8">
                {(step > 0 || editingFromReview) && (
                  <Button
                    variant="outline"
                    className="h-14 w-full sm:w-auto px-8 has-[>svg]:px-8 rounded-full"
                    disabled={isSaving}
                    onClick={() => {
                      if (editingFromReview) {
                        setEditingFromReview(false);
                        setShowReview(true);
                        setFinished(true);
                      } else {
                        setStep((s) => s - 1);
                      }
                    }}>
                    <ArrowLeft className="h-4 w-4 mr-2" />
                    {editingFromReview ? 'Back to review' : 'Back'}
                  </Button>
                )}
                <Button
                  className="h-14 w-full sm:flex-1 rounded-full"
                  disabled={
                    isSaving ||
                    (step === 0 && !canContinueFirstStep) ||
                    (q.id === 'logo' && googleLogoLoading)
                  }
                  onClick={handleContinue}>
                  {isSaving ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : null}
                  {isSaving
                    ? 'Saving...'
                    : editingFromReview
                    ? 'Save and review'
                    : isLastRegularQuestion && !websiteOnboardingRequired
                    ? 'Finish'
                    : 'Continue'}
                  {!isSaving && <ArrowRight className="h-4 w-4 ml-2" />}
                </Button>
              </div>
            </div>
          )}
        </div>
      </main>

      <footer className="py-6 text-center">
        <p className="text-xs text-muted-foreground">© 2026 Adler Local</p>
      </footer>
    </div>
  );
};

export default Onboarding;
